/**
 * Sanitize email-template HTML before persistence / preview.
 *
 * Preserves Jinja2 tokens (`{{ ... }}`, `{% ... %}`) by swapping them for
 * opaque placeholders during DOMPurify, then restoring them unchanged.
 */
import createDOMPurify from 'dompurify';

const JINJA_TOKEN_RE = /(\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\})/g;

const ALLOWED_TAGS = [
  'a', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'hr', 'i', 'li', 'mark', 'ol', 'p', 'pre', 's', 'span', 'strong', 'sub', 'sup',
  'table', 'tbody', 'td', 'th', 'thead', 'tr', 'u', 'ul',
];

const ALLOWED_ATTR = [
  'href', 'target', 'rel', 'style', 'class', 'colspan', 'rowspan',
  'align', 'valign', 'width', 'height', 'border', 'cellpadding', 'cellspacing',
];

const ALLOWED_CSS = new Set([
  'background', 'background-color', 'border', 'border-radius', 'border-color',
  'border-style', 'border-width', 'color', 'display', 'font-family', 'font-size',
  'font-style', 'font-weight', 'letter-spacing', 'line-height', 'margin',
  'margin-bottom', 'margin-left', 'margin-right', 'margin-top', 'padding',
  'padding-bottom', 'padding-left', 'padding-right', 'padding-top',
  'text-align', 'text-decoration', 'white-space', 'width', 'max-width',
]);

function getDOMPurify() {
  if (typeof window === 'undefined') {
    throw new Error('sanitizeEmailHtml requires a browser DOM');
  }
  // CJS/ESM interop: package may export the factory or a ready instance.
  if (typeof createDOMPurify === 'function' && typeof createDOMPurify.sanitize !== 'function') {
    return createDOMPurify(window);
  }
  return createDOMPurify.default && typeof createDOMPurify.default.sanitize === 'function'
    ? createDOMPurify.default
    : createDOMPurify;
}

/** Opaque https placeholder — survives URI allowlists in href/src attributes. */
function jinjaPlaceholder(idx) {
  return `https://jinja.local/token/${idx}`;
}

/** @param {string} html */
export function protectJinjaTokens(html) {
  const tokens = [];
  const protectedHtml = String(html || '').replace(JINJA_TOKEN_RE, (match) => {
    const idx = tokens.length;
    tokens.push(match);
    return jinjaPlaceholder(idx);
  });
  return { protectedHtml, tokens };
}

/** @param {string} html @param {string[]} tokens */
export function restoreJinjaTokens(html, tokens) {
  let out = String(html || '');
  // Restore highest indices first so token/10 is not clobbered by token/1.
  for (let idx = tokens.length - 1; idx >= 0; idx -= 1) {
    out = out.split(jinjaPlaceholder(idx)).join(tokens[idx]);
  }
  return out;
}

/** Strip dangerous CSS (expression, url(javascript:), behavior, etc.). */
function sanitizeInlineStyle(styleValue) {
  if (!styleValue || typeof styleValue !== 'string') return '';
  const parts = styleValue.split(';').map((p) => p.trim()).filter(Boolean);
  const kept = [];
  for (const part of parts) {
    const colon = part.indexOf(':');
    if (colon <= 0) continue;
    const prop = part.slice(0, colon).trim().toLowerCase();
    const value = part.slice(colon + 1).trim();
    if (!ALLOWED_CSS.has(prop)) continue;
    const lower = value.toLowerCase();
    if (
      lower.includes('expression(')
      || lower.includes('javascript:')
      || lower.includes('vbscript:')
      || lower.includes('behavior:')
      || lower.includes('-moz-binding')
      || /url\s*\(\s*['"]?\s*javascript:/i.test(value)
    ) {
      continue;
    }
    kept.push(`${prop}: ${value}`);
  }
  return kept.join('; ');
}

/**
 * TipTap often persists bare `<p>` tags. Email clients add default paragraph
 * margins; the in-app editor zeroes them with CSS. Ensure delivered HTML stays tight.
 * Preserves any existing margin* declaration.
 *
 * @param {string} html
 * @returns {string}
 */
export function ensureEmailParagraphMargins(html) {
  if (!html) return '';
  return String(html).replace(/<p(\s[^>]*)?>/gi, (full, attrs = '') => {
    const styleMatch = attrs.match(/\sstyle\s*=\s*(["'])([\s\S]*?)\1/i);
    if (!styleMatch) {
      const trimmed = attrs.trim();
      return trimmed ? `<p ${trimmed} style="margin: 0">` : '<p style="margin: 0">';
    }
    const quote = styleMatch[1];
    const styleVal = styleMatch[2] || '';
    if (/(?:^|;)\s*margin(?:-top|-bottom|-left|-right)?\s*:/i.test(styleVal)) {
      return full;
    }
    const nextStyle = styleVal.trim()
      ? `margin: 0; ${styleVal.trim()}`
      : 'margin: 0';
    const nextAttrs = attrs.replace(
      /\sstyle\s*=\s*(["'])([\s\S]*?)\1/i,
      ` style=${quote}${nextStyle}${quote}`,
    );
    return `<p${nextAttrs}>`;
  });
}

/**
 * @param {string} html
 * @returns {string}
 */
export function sanitizeEmailHtml(html) {
  if (!html) return '';

  const DOMPurify = getDOMPurify();
  const { protectedHtml, tokens } = protectJinjaTokens(html);

  const clean = DOMPurify.sanitize(protectedHtml, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'link', 'meta', 'base', 'svg', 'math'],
    FORBID_ATTR: [
      'onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onsubmit',
      'onchange', 'oninput', 'onkeydown', 'onkeyup', 'onkeypress',
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  });

  // Second pass: scrub style attributes to an email-safe CSS allowlist.
  const withSafeStyles = clean.replace(
    /\sstyle\s*=\s*(["'])([\s\S]*?)\1/gi,
    (_m, quote, styleVal) => {
      const safe = sanitizeInlineStyle(styleVal);
      return safe ? ` style=${quote}${safe}${quote}` : '';
    },
  );

  const restored = restoreJinjaTokens(withSafeStyles, tokens);

  // Final href scan: drop javascript:/data: after Jinja restore.
  const withoutDangerousHref = restored.replace(
    /\shref\s*=\s*(["'])([\s\S]*?)\1/gi,
    (match, quote, href) => {
      const trimmed = String(href).trim();
      if (/^\s*javascript:/i.test(trimmed) || /^\s*vbscript:/i.test(trimmed) || /^\s*data:/i.test(trimmed)) {
        return ` href=${quote}#${quote}`;
      }
      return match;
    },
  );
  return ensureEmailParagraphMargins(withoutDangerousHref);
}

/** HireIQ CTA button inline style used by seeded templates. */
export const EMAIL_CTA_BUTTON_STYLE =
  'background:#0F62FE;color:#fff;padding:10px 18px;border-radius:6px;text-decoration:none;display:inline-block';
